import { useMemo, useState } from 'react';
import Spinner from '../../components/Spinner/Spinner';
import { saveHouseholdData, useHouseholdData } from '../../queries';
import FairnessPanel from './FairnessPanel';
import './HouseholdBoard.css';
import ItemCard from './ItemCard';
import ItemDialog from './ItemDialog';
import ProjectDialog from './ProjectDialog';
import ProjectsPanel from './ProjectsPanel';
import UpcomingUnassigned from './UpcomingUnassigned';
import {
  newId, nextDueDate, nextProjectColor,
} from './householdUtils';
import {
  HouseholdItem, PEOPLE, Person, Project, Status, STATUS_LABELS,
} from './types';

type Tab = 'board' | 'upcoming' | 'projects' | 'fairness';
type AssigneeFilter = 'all' | Person | 'unassigned';

function HouseholdBoard() {
  const [loading, data, error, refetch] = useHouseholdData();
  const [tab, setTab] = useState<Tab>('board');
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>('all');
  const [projectFilter, setProjectFilter] = useState<'all' | string>('all');
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<HouseholdItem | undefined>(undefined);
  const [newItemProjectId, setNewItemProjectId] = useState<string | undefined>(undefined);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const { items, projects } = data;
  const projectsById = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p])),
    [projects]
  );
  const projectColors = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p.color])),
    [projects]
  );

  const persist = async (next: typeof data) => {
    setSaving(true);
    try {
      await saveHouseholdData(next);
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = useMemo(() => items.filter((item) => {
    if (assigneeFilter === 'unassigned' && item.assignee) return false;
    if (assigneeFilter !== 'all' && assigneeFilter !== 'unassigned' && item.assignee !== assigneeFilter) {
      return false;
    }
    if (projectFilter !== 'all' && item.projectId !== projectFilter) return false;
    return true;
  }), [items, assigneeFilter, projectFilter]);

  const columns: Status[] = ['todo', 'in-progress', 'done'];

  const updateItem = (id: string, patch: Partial<HouseholdItem>) => {
    const next = {
      ...data,
      items: items.map((i) => (
        i.id === id ? { ...i, ...patch, updatedAt: new Date().toISOString() } : i
      )),
    };
    persist(next);
  };

  const deleteItem = (id: string) => {
    const next = { ...data, items: items.filter((i) => i.id !== id) };
    persist(next);
  };

  const completeItem = (item: HouseholdItem) => {
    if (!item.assignee) {
      // eslint-disable-next-line no-alert
      window.alert('Assign this to Patrick or Prapti before marking it done, so it counts toward fairness.');
      return;
    }
    const now = new Date().toISOString();
    const updatedItems = items.map((i) => (
      i.id === item.id ? { ...i, status: 'done' as Status, updatedAt: now } : i
    ));

    if (item.recurrence !== 'none') {
      updatedItems.push({
        ...item,
        id: newId(),
        status: 'todo',
        assignee: undefined,
        dueDate: item.recurrence !== 'unspecified' && item.dueDate
          ? nextDueDate(item.dueDate, item.recurrence) : undefined,
        createdAt: now,
        updatedAt: now,
      });
    }

    const next = {
      ...data,
      items: updatedItems,
      completions: [
        ...data.completions,
        {
          id: newId(),
          itemId: item.id,
          title: item.title,
          assignee: item.assignee,
          effort: item.effort,
          completedAt: now,
        }
      ],
    };
    persist(next);
  };

  const openNewItemDialog = (projectId?: string) => {
    setEditingItem(undefined);
    setNewItemProjectId(projectId);
    setItemDialogOpen(true);
  };

  const openEditItemDialog = (item: HouseholdItem) => {
    setEditingItem(item);
    setItemDialogOpen(true);
  };

  const handleSaveItem = (values: {
    title: string;
    description: string;
    status: Status;
    assignee?: Person;
    dueDate?: string;
    effort?: number;
    recurrence: HouseholdItem['recurrence'];
    projectId?: string;
  }) => {
    const now = new Date().toISOString();
    let nextItems: HouseholdItem[];
    if (editingItem) {
      nextItems = items.map((i) => (
        i.id === editingItem.id ? { ...i, ...values, updatedAt: now } : i
      ));
    } else {
      nextItems = [
        ...items,
        {
          id: newId(), ...values, createdAt: now, updatedAt: now,
        }
      ];
    }
    setItemDialogOpen(false);
    persist({ ...data, items: nextItems });
  };

  const openNewProjectDialog = () => {
    setEditingProject(undefined);
    setProjectDialogOpen(true);
  };

  const openEditProjectDialog = (project: Project) => {
    setEditingProject(project);
    setProjectDialogOpen(true);
  };

  const handleSaveProject = (values: { title: string; description: string; color: string }) => {
    const now = new Date().toISOString();
    let nextProjects: Project[];
    if (editingProject) {
      nextProjects = projects.map((p) => (
        p.id === editingProject.id ? { ...p, ...values, updatedAt: now } : p
      ));
    } else {
      nextProjects = [
        ...projects,
        {
          id: newId(), ...values, createdAt: now, updatedAt: now,
        }
      ];
    }
    setProjectDialogOpen(false);
    persist({ ...data, projects: nextProjects });
  };

  const deleteProject = (project: Project) => {
    const next = {
      ...data,
      projects: projects.filter((p) => p.id !== project.id),
      items: items.map((i) => (
        i.projectId === project.id ? { ...i, projectId: undefined } : i
      )),
    };
    if (projectFilter === project.id) setProjectFilter('all');
    persist(next);
  };

  if (loading && items.length === 0 && projects.length === 0) {
    return (
      <div className="hh-page hh-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="hh-page">
      <header className="hh-header">
        <h1>Household Board</h1>
        <div className="hh-header-actions">
          {saving && <span className="hh-saving">Saving…</span>}
          <button type="button" className="hh-btn hh-btn-primary" onClick={() => openNewItemDialog()}>
            + New Task
          </button>
          <button type="button" className="hh-btn hh-btn-secondary" onClick={openNewProjectDialog}>
            + New Project
          </button>
        </div>
      </header>

      {error && (
        <div className="hh-error">
          Couldn&apos;t load data from storage. Changes may not save until this is fixed.
        </div>
      )}

      <nav className="hh-tabs">
        <button type="button" className={tab === 'board' ? 'active' : ''} onClick={() => setTab('board')}>Board</button>
        <button type="button" className={tab === 'upcoming' ? 'active' : ''} onClick={() => setTab('upcoming')}>Upcoming &amp; Unassigned</button>
        <button type="button" className={tab === 'projects' ? 'active' : ''} onClick={() => setTab('projects')}>Projects</button>
        <button type="button" className={tab === 'fairness' ? 'active' : ''} onClick={() => setTab('fairness')}>Fairness</button>
      </nav>

      {tab === 'board' && (
        <>
          <div className="hh-filters">
            <span>Show:</span>
            <button type="button" className={assigneeFilter === 'all' ? 'active' : ''} onClick={() => setAssigneeFilter('all')}>All</button>
            {PEOPLE.map((p) => (
              <button key={p} type="button" className={assigneeFilter === p ? 'active' : ''} onClick={() => setAssigneeFilter(p)}>{p}</button>
            ))}
            <button type="button" className={assigneeFilter === 'unassigned' ? 'active' : ''} onClick={() => setAssigneeFilter('unassigned')}>Unassigned</button>

            {projects.length > 0 && (
              <>
                <span className="hh-filter-sep">Project:</span>
                <button type="button" className={projectFilter === 'all' ? 'active' : ''} onClick={() => setProjectFilter('all')}>All</button>
                {projects.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={projectFilter === p.id ? 'active' : ''}
                    onClick={() => setProjectFilter(p.id)}
                  >
                    <span className="hh-color-dot" style={{ backgroundColor: p.color }} />
                    {p.title}
                  </button>
                ))}
              </>
            )}
          </div>

          <div className="hh-board">
            {columns.map((col) => (
              <div key={col} className="hh-column">
                <h2>
                  {STATUS_LABELS[col]}
                  {' '}
                  (
                  {filteredItems.filter((i) => i.status === col).length}
                  )
                </h2>
                <div className="hh-column-items">
                  {filteredItems
                    .filter((i) => i.status === col)
                    .sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'))
                    .map((item) => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        projectTitle={item.projectId
                          ? projectsById[item.projectId]?.title : undefined}
                        projectColor={item.projectId ? projectColors[item.projectId] : undefined}
                        onStatusChange={(status) => updateItem(item.id, { status })}
                        onAssigneeChange={(assignee) => updateItem(item.id, { assignee })}
                        onEdit={() => openEditItemDialog(item)}
                        onDelete={() => deleteItem(item.id)}
                        onComplete={() => completeItem(item)}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'upcoming' && (
        <UpcomingUnassigned
          items={items}
          projectColors={projectColors}
          onAssign={(id, assignee) => updateItem(id, { assignee })}
          onEdit={openEditItemDialog}
        />
      )}

      {tab === 'projects' && (
        <ProjectsPanel
          projects={projects}
          items={items}
          onAdd={openNewProjectDialog}
          onEdit={openEditProjectDialog}
          onDelete={deleteProject}
        />
      )}

      {tab === 'fairness' && <FairnessPanel completions={data.completions} />}

      {itemDialogOpen && (
        <ItemDialog
          item={editingItem}
          projects={projects}
          defaultProjectId={newItemProjectId}
          onClose={() => setItemDialogOpen(false)}
          onSave={handleSaveItem}
        />
      )}

      {projectDialogOpen && (
        <ProjectDialog
          project={editingProject}
          defaultColor={nextProjectColor(projects)}
          onClose={() => setProjectDialogOpen(false)}
          onSave={handleSaveProject}
        />
      )}
    </div>
  );
}

export default HouseholdBoard;
